import type { OutputData } from "@editorjs/editorjs";

const DEFAULT_EMPTY_CONTENT = `
    <div style="padding:0 20px 24px;">
      <div
        style="
          width:min(100%,900px);
          margin:0 auto;
          padding:0;
          background:transparent;
          color:#71717a;
          font-size:15px;
          line-height:1.6;
        "
      >
        Your custom page content preview will appear here.
      </div>
    </div>
  `.trim();

const ROOT_WRAPPER_STYLE = `
  padding:20px 20px 32px;
  color:#18181b;
  font-size:15px;
  line-height:1.65;
`;

const ROOT_INNER_STYLE = `
  width:min(100%,900px);
  margin:0 auto;
`;

const STACK_STYLE = `
  display:flex;
  flex-direction:column;
  gap:16px;
`;

const CARD_STYLE = `
  margin:0;
  border:0;
  border-radius:0;
  background:transparent;
  box-shadow:none;
  overflow:hidden;
`;

const BLOCK_SURFACE_STYLE = `
  margin:0;
  padding:0;
  border:0;
  border-radius:0;
  background:transparent;
  box-shadow:none;
`;

const HEADER_STYLES: Record<number, string> = {
  1: "font-size:clamp(2rem,4vw,3rem);line-height:1.08;font-weight:800;letter-spacing:-0.05em;",
  2: "font-size:clamp(1.625rem,3.2vw,2.375rem);line-height:1.12;font-weight:780;letter-spacing:-0.04em;",
  3: "font-size:clamp(1.375rem,2.6vw,1.875rem);line-height:1.2;font-weight:720;letter-spacing:-0.03em;",
  4: "font-size:clamp(1.125rem,2vw,1.375rem);line-height:1.28;font-weight:700;letter-spacing:-0.02em;",
  5: "font-size:1rem;line-height:1.35;font-weight:700;letter-spacing:-0.01em;",
  6: "font-size:0.875rem;line-height:1.45;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#52525b;",
};

const wrapInRoot = (content: string) =>
  `
    <div style="${ROOT_WRAPPER_STYLE}">
      <div style="${ROOT_INNER_STYLE}">
        <div style="${STACK_STYLE}">
          ${content}
        </div>
      </div>
    </div>
  `.trim();

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sanitizeUrl = (value?: string) => {
  if (!value) {
    return "";
  }

  const trimmed = value.trim();

  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("//")
  ) {
    return trimmed;
  }

  return "";
};

const renderList = (data: Record<string, unknown>) => {
  const style = data.style === "ordered" ? "ol" : "ul";
  const items = Array.isArray(data.items) ? data.items : [];
  const renderedItems = items
    .map((item) => {
      if (typeof item === "string") {
        return `<li style="margin:0;">${item}</li>`;
      }

      if (item && typeof item === "object" && "content" in item) {
        return `<li style="margin:0;">${String(item.content ?? "")}</li>`;
      }

      return "";
    })
    .join("");

  if (!renderedItems) {
    return "";
  }

  const listStyle =
    style === "ol"
      ? "margin:0;padding-left:1.4em;display:flex;flex-direction:column;gap:10px;color:#27272a;"
      : "margin:0;padding-left:1.2em;display:flex;flex-direction:column;gap:10px;color:#27272a;";

  return `
    <div style="${BLOCK_SURFACE_STYLE}">
      <${style} style="${listStyle}">
        ${renderedItems}
      </${style}>
    </div>
  `;
};

const renderChecklist = (data: Record<string, unknown>) => {
  const items = Array.isArray(data.items) ? data.items : [];
  const renderedItems = items
    .map((item) => {
      if (!item || typeof item !== "object") {
        return "";
      }

      const text = String((item as { text?: unknown }).text ?? "");
      const checked = Boolean((item as { checked?: unknown }).checked);

      return `
        <li style="display:flex;align-items:flex-start;gap:12px;list-style:none;margin:0;">
          <span
            aria-hidden="true"
            style="
              margin-top:2px;
              display:inline-flex;
              height:20px;
              width:20px;
              flex-shrink:0;
              align-items:center;
              justify-content:center;
              border-radius:6px;
              border:1px solid ${checked ? "#2563eb" : "#d4d4d8"};
              background:${checked ? "#2563eb" : "#ffffff"};
              color:#ffffff;
              font-size:12px;
              font-weight:700;
            "
          >
            ${checked ? "✓" : ""}
          </span>
          <span style="${checked ? "color:#71717a;text-decoration:line-through;" : "color:#27272a;"}">
            ${text}
          </span>
        </li>
      `;
    })
    .filter(Boolean)
    .join("");

  return renderedItems
    ? `
      <div style="${BLOCK_SURFACE_STYLE}">
        <ul style="margin:0;padding:0;display:flex;flex-direction:column;gap:12px;">
          ${renderedItems}
        </ul>
      </div>
    `
    : "";
};

const renderTable = (data: Record<string, unknown>) => {
  const content = Array.isArray(data.content) ? data.content : [];
  const withHeadings = Boolean(data.withHeadings);
  const stretched = Boolean(data.stretched);

  if (content.length === 0) {
    return "";
  }

  const renderRow = (row: unknown[], cellTag: "td" | "th") => {
    const cells = row
      .map(
        (cell) =>
          `<${cellTag} style="border:1px solid #e4e4e7;padding:12px 14px;text-align:left;vertical-align:top;font-size:14px;line-height:1.5;color:#27272a;${cellTag === "th" ? "font-weight:700;color:#18181b;background:#f9fafb;" : "background:#ffffff;"}">${String(cell ?? "")}</${cellTag}>`,
      )
      .join("");

    return `<tr>${cells}</tr>`;
  };

  const [firstRow, ...bodyRows] = content.filter(Array.isArray) as unknown[][];
  const headHtml =
    withHeadings && firstRow
      ? `<thead style="background:#f4f4f5;">${renderRow(firstRow, "th")}</thead>`
      : "";
  const bodySource = withHeadings ? bodyRows : [firstRow, ...bodyRows];
  const bodyHtml = bodySource.length
    ? `<tbody>${bodySource.map((row) => renderRow(row, "td")).join("")}</tbody>`
    : "";

  return `
    <div style="${CARD_STYLE}">
      <div style="padding:10px;overflow-x:auto;">
        <table style="width:${stretched ? "100%" : "auto"};min-width:100%;border-collapse:collapse;border-spacing:0;overflow:hidden;border-radius:18px;">
          ${headHtml}
          ${bodyHtml}
        </table>
      </div>
    </div>
  `;
};

const renderEmbed = (data: Record<string, unknown>) => {
  const embedUrl = sanitizeUrl(String(data.embed ?? ""));

  if (!embedUrl) {
    return "";
  }

  const caption = String(data.caption ?? "").trim();

  return `
    <figure style="${CARD_STYLE}">
      <div style="position:relative;width:100%;padding-top:56.25%;background:#09090b;">
        <iframe
          src="${escapeHtml(embedUrl)}"
          style="position:absolute;inset:0;width:100%;height:100%;border:0;border-radius:16px;"
          allowfullscreen
        ></iframe>
      </div>
      ${caption ? `<figcaption style="padding:14px 16px 16px;color:#71717a;font-size:13px;line-height:1.5;background:#ffffff;">${caption}</figcaption>` : ""}
    </figure>
  `;
};

const renderImage = (data: Record<string, unknown>) => {
  const file =
    data.file && typeof data.file === "object"
      ? (data.file as { url?: string; fileId?: string })
      : undefined;
  const imageUrl = sanitizeUrl(file?.url ?? String(data.url ?? ""));

  if (!imageUrl) {
    return "";
  }

  const caption = String(data.caption ?? "").trim();
  const withBorder = Boolean(data.withBorder);
  const withBackground = Boolean(data.withBackground);
  const wrapperStyle = withBackground
    ? "padding:18px;background:linear-gradient(180deg,#f9fafb 0%,#f4f4f5 100%);"
    : "padding:0;";
  const imageStyle = [
    "display:block;",
    "width:100%;",
    "max-width:100%;",
    "height:auto;",
    "border-radius:20px;",
    withBorder ? "border:1px solid #d4d4d8;" : "",
    "background:#ffffff;",
    "object-fit:cover;",
  ].join("");

  return `
    <figure style="${CARD_STYLE}">
      <div style="${wrapperStyle}">
        <img
          src="${escapeHtml(imageUrl)}"
          alt="${escapeHtml(caption)}"
          style="${imageStyle}"
          ${file?.fileId ? `data-file-id="${escapeHtml(file.fileId)}"` : ""}
        />
      </div>
      ${caption ? `<figcaption style="padding:14px 16px 16px;color:#71717a;font-size:13px;line-height:1.5;background:#ffffff;">${caption}</figcaption>` : ""}
    </figure>
  `;
};

const stripHtmlTags = (value: string): string => {
  let result = "";
  let insideTag = false;

  for (const char of value) {
    if (char === "<") {
      insideTag = true;
      continue;
    }

    if (char === ">") {
      insideTag = false;
      continue;
    }

    if (!insideTag) {
      result += char;
    }
  }

  return result;
};

const normalizeTextContent = (value: unknown): string =>
  stripHtmlTags(String(value ?? ""))
    .split("&nbsp;")
    .join(" ")
    .split("&#160;")
    .join(" ")
    .split("\u00a0")
    .join(" ")
    .trim();

const hasTextContent = (value: unknown): boolean =>
  normalizeTextContent(value).length > 0;

const hasUrlContent = (value: unknown): boolean =>
  typeof value === "string" && value.trim().length > 0;

const hasListContent = (items: unknown): boolean =>
  Array.isArray(items) &&
  items.some((item) => {
    if (typeof item === "string") {
      return hasTextContent(item);
    }

    if (!item || typeof item !== "object") {
      return false;
    }

    const itemRecord = item as Record<string, unknown>;
    return (
      hasTextContent(itemRecord.content) ||
      hasTextContent(itemRecord.text) ||
      hasListContent(itemRecord.items)
    );
  });

const hasTableContent = (content: unknown): boolean =>
  Array.isArray(content) &&
  content.some(
    (row) => Array.isArray(row) && row.some((cell) => hasTextContent(cell)),
  );

const hasImageContent = (data: Record<string, unknown>): boolean => {
  const file =
    data.file && typeof data.file === "object"
      ? (data.file as Record<string, unknown>)
      : undefined;

  return hasUrlContent(file?.url) || hasUrlContent(data.url);
};

const hasMeaningfulBlockContent = (
  type: string,
  data: Record<string, unknown> | undefined,
): boolean => {
  const blockData = data ?? {};

  switch (type) {
    case "paragraph":
    case "header":
      return hasTextContent(blockData.text);
    case "quote":
      return (
        hasTextContent(blockData.text) || hasTextContent(blockData.caption)
      );
    case "list":
    case "checklist":
      return hasListContent(blockData.items);
    case "table":
      return hasTableContent(blockData.content);
    case "embed":
      return hasUrlContent(blockData.embed);
    case "image":
      return hasImageContent(blockData);
    default:
      return Object.values(blockData).some((value) => {
        if (typeof value === "string") {
          return hasTextContent(value);
        }

        return false;
      });
  }
};

const renderBlock = (
  type: string,
  data: Record<string, unknown> | undefined,
): string => {
  const blockData = data ?? {};

  switch (type) {
    case "paragraph":
      return `
        <div style="${BLOCK_SURFACE_STYLE}">
          <p style="margin:0;color:#27272a;text-indent:1.75em;text-wrap:pretty;">
            ${String(blockData.text ?? "")}
          </p>
        </div>
      `;
    case "header": {
      const level = Number(blockData.level ?? 2);
      const safeLevel = Math.min(6, Math.max(1, level));
      return `
        <div style="margin:${safeLevel <= 2 ? "10px" : "6px"} 0 ${safeLevel <= 3 ? "2px" : "0"};">
          <h${safeLevel} style="margin:0;color:#09090b;${HEADER_STYLES[safeLevel]}">
            ${String(blockData.text ?? "")}
          </h${safeLevel}>
        </div>
      `;
    }
    case "list":
      return renderList(blockData);
    case "quote":
      return `
        <blockquote
          style="
            margin:0;
            padding:20px 20px 20px 22px;
            border-left:4px solid #18181b;
            border-radius:22px;
            background:linear-gradient(135deg,#fafafa 0%,#f4f4f5 100%);
            color:#3f3f46;
          "
        >
          <p style="margin:0;color:#27272a;font-size:16px;line-height:1.7;text-wrap:pretty;">${String(blockData.text ?? "")}</p>
          ${blockData.caption ? `<cite style="display:block;margin-top:10px;color:#71717a;font-size:13px;font-style:normal;">${String(blockData.caption)}</cite>` : ""}
        </blockquote>
      `;
    case "delimiter":
      return `
        <div style="padding:2px 0;">
          <hr style="border:none;border-top:1px solid #e4e4e7;margin:0;" />
        </div>
      `;
    case "embed":
      return renderEmbed(blockData);
    case "image":
      return renderImage(blockData);
    case "checklist":
      return renderChecklist(blockData);
    case "table":
      return renderTable(blockData);
    default:
      return "";
  }
};

export const getEmptyRichEditorData = (): OutputData => ({
  blocks: [],
});

export const hasRichEditorContent = (data?: OutputData | null): boolean =>
  (data?.blocks ?? []).some((block) =>
    hasMeaningfulBlockContent(block.type, block.data),
  );

export const renderRichEditorContent = (
  data?: OutputData | null,
  fallbackHtml?: string,
  options?: {
    wrapInRoot?: boolean;
  },
) => {
  const blocks = data?.blocks ?? [];
  const shouldWrapInRoot = options?.wrapInRoot ?? true;

  if (blocks.length === 0) {
    return fallbackHtml?.trim() || DEFAULT_EMPTY_CONTENT;
  }

  const rendered = blocks
    .map((block) => renderBlock(block.type, block.data))
    .filter(Boolean)
    .join("\n");

  if (!rendered) {
    return fallbackHtml?.trim() || DEFAULT_EMPTY_CONTENT;
  }

  return shouldWrapInRoot ? wrapInRoot(rendered) : rendered;
};

export type ImageUrlResolver = (fileId: string) => Promise<string>;

/**
 * Image blocks store a signed file URL next to `fileId`. The signature expires
 * (3600 s by default), so a saved page shows broken images when reopened later.
 * Returns data with every image URL re-signed by `fileId`; blocks without
 * `fileId` and ids that fail to resolve keep their stored URL. The input is not
 * mutated; `changed` tells whether any URL differs.
 */
export const refreshImageUrls = async (
  data: OutputData,
  resolveUrl: ImageUrlResolver,
): Promise<{ data: OutputData; changed: boolean; failed: number }> => {
  const blocks = Array.isArray(data?.blocks) ? data.blocks : [];
  const fileIds = new Set<string>();

  for (const block of blocks) {
    const fileId = block.type === "image" ? block.data?.file?.fileId : null;

    if (typeof fileId === "string" && fileId) {
      fileIds.add(fileId);
    }
  }

  if (fileIds.size === 0) {
    return { data, changed: false, failed: 0 };
  }

  const urls = new Map<string, string>();
  let failed = 0;

  await Promise.all(
    [...fileIds].map(async (fileId) => {
      try {
        const url = await resolveUrl(fileId);

        if (typeof url === "string" && url) {
          urls.set(fileId, url);
        } else {
          failed += 1;
        }
      } catch {
        failed += 1; // keep the stored URL
      }
    }),
  );

  let changed = false;
  const nextBlocks = blocks.map((block) => {
    const file = block.type === "image" ? block.data?.file : undefined;
    const url = typeof file?.fileId === "string" ? urls.get(file.fileId) : null;

    if (!url || url === file.url) {
      return block;
    }

    changed = true;
    return { ...block, data: { ...block.data, file: { ...file, url } } };
  });

  return changed
    ? { data: { ...data, blocks: nextBlocks }, changed, failed }
    : { data, changed, failed };
};

/**
 * contentData with re-signed image URLs plus the guest HTML that goes with it.
 * When any URL changed, `content` is rebuilt from the new contentData, so the
 * guest HTML never keeps the expired links; otherwise `storedContent` is kept.
 */
export const buildContentWithFreshImages = async (
  data: OutputData,
  storedContent: string,
  resolveUrl: ImageUrlResolver,
): Promise<{
  contentData: OutputData;
  content: string;
  changed: boolean;
  failed: number;
}> => {
  const {
    data: contentData,
    changed,
    failed,
  } = await refreshImageUrls(data, resolveUrl);

  return {
    contentData,
    content: changed ? renderRichEditorContent(contentData) : storedContent,
    changed,
    failed,
  };
};
